import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, Collapse, Divider, Input, InputNumber, Modal, Space, Spin, Steps, Switch, Table, Tabs, Tag, Typography, message } from "antd";
import { DeleteOutlined, SendOutlined, StarFilled } from "@ant-design/icons";
import axios from "axios";
import TrainingChart from "./TrainingChart";
import LiquidGlass from "./LiquidGlass";
import RequestPremium from "./RequestPremium";

const { Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

function AiIndividualSessionBuilder({ onClose, initialCoachMessage, initialState, bootstrap, appFtp, appIsAdmin, appIsPremium, onPersistState, onAfterSave, onRequestPremium }) {
  // Minimal markdown support for conversation: **bold**, *italic* (no links/html), preserve newlines.
  const renderMarkdownLiteInline = useCallback((line, keyPrefix = "") => {
    const s = String(line ?? "");
    const out = [];
    let i = 0;
    let k = 0;
    const pushText = (txt) => {
      if (!txt) return;
      out.push(<React.Fragment key={`${keyPrefix}t-${k++}`}>{txt}</React.Fragment>);
    };
    while (i < s.length) {
      // Bold: **text**
      if (s.startsWith("**", i)) {
        const j = s.indexOf("**", i + 2);
        if (j !== -1) {
          const inner = s.slice(i + 2, j);
          out.push(
            <strong key={`${keyPrefix}b-${k++}`}>
              {inner}
            </strong>
          );
          i = j + 2;
          continue;
        }
        // Unmatched ** => treat first * as literal
        pushText("*");
        i += 1;
        continue;
      }
      // Italic: *text* (but don't start on **, handled above)
      if (s[i] === "*") {
        const j = s.indexOf("*", i + 1);
        if (j !== -1) {
          const inner = s.slice(i + 1, j);
          out.push(
            <em key={`${keyPrefix}i-${k++}`}>
              {inner}
            </em>
          );
          i = j + 1;
          continue;
        }
        // Unmatched * => literal
        pushText("*");
        i += 1;
        continue;
      }

      const nextBold = s.indexOf("**", i);
      const nextIt = s.indexOf("*", i);
      let next = -1;
      if (nextBold !== -1 && nextIt !== -1) next = Math.min(nextBold, nextIt);
      else next = nextBold !== -1 ? nextBold : nextIt;
      if (next === -1) next = s.length;
      pushText(s.slice(i, next));
      i = next;
    }
    return out;
  }, []);

  const renderMarkdownLite = useCallback(
    (content) => {
      const s = String(content ?? "");
      const lines = s.split("\n");
      return lines.map((line, idx) => (
        <React.Fragment key={`ml-${idx}`}>
          {renderMarkdownLiteInline(line, `ml-${idx}-`)}
          {idx < lines.length - 1 ? <br /> : null}
        </React.Fragment>
      ));
    },
    [renderMarkdownLiteInline]
  );

  const [currentStep, setCurrentStep] = useState(0);

  // Chat / intake
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [lastReformulation, setLastReformulation] = useState("");
  const [intakeIsEnough, setIntakeIsEnough] = useState(false);
  const convoScrollRef = useRef(null);
  const hasBootstrappedRef = useRef(false);
  const restoredFromStateRef = useRef(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [creditsRemaining, setCreditsRemaining] = useState(null);
  const [, setCreditsLimit] = useState(null);
  const [userInfoLoaded, setUserInfoLoaded] = useState(false);
  const premiumModalRef = useRef(null);

  // Admin-only: last GPT debug blobs
  const [debugIntake, setDebugIntake] = useState(null); // {gpt_in,gpt_out}
  const [debugGenerate, setDebugGenerate] = useState(null); // {gpt_in,gpt_out}

  const capDebug = useCallback((dbg) => {
    try {
      if (!dbg || typeof dbg !== "object") return null;
      const capStr = (s, n = 60000) => {
        const v = typeof s === "string" ? s : s == null ? "" : String(s);
        if (v.length <= n) return v;
        return `${v.slice(0, n - 20)}\n... (truncated) ...`;
      };
      const next = JSON.parse(JSON.stringify(dbg));
      if (next?.gpt_in?.messages && Array.isArray(next.gpt_in.messages)) {
        next.gpt_in.messages = next.gpt_in.messages.map((m) => ({
          ...(m || {}),
          content: capStr(m?.content, 60000),
        }));
      }
      if (next?.gpt_out?.raw) next.gpt_out.raw = capStr(next.gpt_out.raw, 60000);
      if (next?.gpt_out?.error) next.gpt_out.error = capStr(next.gpt_out.error, 8000);
      return next;
    } catch {
      return dbg;
    }
  }, []);

  const DebugPanel = useCallback(
    ({ debug }) => {
      if (!isAdmin) return null;
      if (!debug || typeof debug !== "object") return null;
      const gin = debug.gpt_in ?? null;
      const gout = debug.gpt_out ?? null;
      return (
        <Collapse size="small" style={{ marginTop: 10 }}>
          <Panel header="Debug" key="debug">
            <Tabs
              items={[
                {
                  key: "in",
                  label: "GPT in",
                  children: (
                    <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12 }}>
                      {JSON.stringify(gin, null, 2)}
                    </pre>
                  ),
                },
                {
                  key: "out",
                  label: "GPT out",
                  children: (
                    <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12 }}>
                      {JSON.stringify(gout, null, 2)}
                    </pre>
                  ),
                },
              ]}
            />
          </Panel>
        </Collapse>
      );
    },
    [isAdmin]
  );

  // Workout generation
  const [generateNote, setGenerateNote] = useState("");
  const [coachComment, setCoachComment] = useState("");
  const [workout, setWorkout] = useState(null);
  const [generating, setGenerating] = useState(false);
  // Step editing drafts: keep user raw input visible even when invalid (do NOT overwrite workout until valid)
  // shape: { [stepIdx]: { duration?: string, watt?: string, rpm?: string } }
  const [stepDrafts, setStepDrafts] = useState({});

  // Saving
  const [category, setCategory] = useState("AI Single Sessions");
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  // Display-only: allow switching between %FTP and absolute Watts. Storage remains %FTP (ref_ftp=100).
  const [powerDisplayMode, setPowerDisplayMode] = useState("percent"); // "percent" | "watts"
  const [displayFtp, setDisplayFtp] = useState(null);
  const hasAnyUserMessage = useMemo(() => (chatMessages || []).some((m) => m && m.role === "user" && String(m.content || "").trim()), [chatMessages]);

  const emitPersistState = useCallback(
    (overrides = {}) => {
      try {
        if (typeof onPersistState !== "function") return;
        const ssDraft = {
          category: category,
          coach_comment: coachComment,
          workout: workout,
          intake_is_enough: intakeIsEnough,
        };
        const snapshot = {
          status: "in_progress",
          mode: "single_session",
          current_step: currentStep,
          intro_message: (initialCoachMessage || "").trim() || undefined,
          conversation: chatMessages,
          last_reformulation: lastReformulation,
          intake_is_enough: intakeIsEnough,
          generate_note: generateNote,
          coach_comment: coachComment,
          category: category,
          workout: workout,
          single_session: ssDraft,
          debug: isAdmin
            ? {
              intake: debugIntake,
              generate: debugGenerate,
            }
            : undefined,
          ...overrides,
        };
        onPersistState(snapshot);
      } catch {
        // ignore
      }
    },
    [
      onPersistState,
      currentStep,
      initialCoachMessage,
      chatMessages,
      lastReformulation,
      intakeIsEnough,
      generateNote,
      coachComment,
      category,
      workout,
      isAdmin,
      debugIntake,
      debugGenerate,
    ]
  );

  const stepItems = useMemo(
    () => [
      { title: "Coach Intake", description: "Define one workout" },
      { title: "Workout", description: "Generate one session" },
    ],
    []
  );

  // --- Leave / draft dirty-check (manual edits) -----------------------------------
  const draftBaselineRef = useRef(null); // JSON string snapshot
  const baselineReadyRef = useRef(false);

  const currentDraftSnapshot = useMemo(() => {
    try {
      const w = workout && typeof workout === "object" ? workout : null;
      const steps = Array.isArray(w?.steps) ? w.steps : [];
      const normSteps = steps.map((st) => ({
        title: String(st?.title || "").trim(),
        comment: String(st?.comment || "").trim(),
        duration: Number(st?.duration) || 0,
        watt: Number(st?.watt) || 0,
        rpm: Number(st?.rpm) || 0,
      }));
      const sd = stepDrafts && typeof stepDrafts === "object" ? stepDrafts : {};
      // keep stable: sort keys to avoid JSON order differences
      const sdKeys = Object.keys(sd).sort((a, b) => String(a).localeCompare(String(b)));
      const sdOut = {};
      sdKeys.forEach((k) => {
        sdOut[k] = sd[k];
      });
      return JSON.stringify({
        category: String(category || "").trim(),
        title: String(w?.title || "").trim(),
        description: String(w?.description || "").trim(),
        steps: normSteps,
        stepDrafts: sdOut,
      });
    } catch {
      return "";
    }
  }, [category, stepDrafts, workout]);

  const setDraftBaselineToCurrent = useCallback(() => {
    if (!currentDraftSnapshot) return;
    draftBaselineRef.current = currentDraftSnapshot;
    baselineReadyRef.current = true;
  }, [currentDraftSnapshot]);

  useEffect(() => {
    // Establish baseline once a workout exists (generated or restored) so Leave doesn't prompt immediately.
    if (baselineReadyRef.current) return;
    if (!workout) return;
    if (!currentDraftSnapshot) return;
    setDraftBaselineToCurrent();
  }, [currentDraftSnapshot, setDraftBaselineToCurrent, workout]);

  const doLeaveNoPrompt = useCallback(() => {
    try {
      emitPersistState();
    } catch {
      // ignore
    }
    try {
      if (typeof onClose === "function") onClose();
    } catch {
      // ignore
    }
  }, [emitPersistState, onClose]);

  const handleBackToIntake = useCallback(() => {
    // If nothing has been generated yet, don't nag the user.
    if (!workout) {
      setCurrentStep(0);
      return;
    }

    Modal.confirm({
      title: "Go back to intake?",
      content: (
        <span style={{ whiteSpace: "pre-wrap" }}>
          {"All generated workout and manual changes will be lost."}
        </span>
      ),
      okText: "Discard and go back",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: () => {
        try {
          setWorkout(null);
          setCoachComment("");
          setStepDrafts({});
        } catch {
          // ignore
        }
        try {
          emitPersistState({
            current_step: 0,
            workout: null,
            coach_comment: "",
          });
        } catch {
          // ignore
        }
        setCurrentStep(0);
      },
    });
  }, [emitPersistState, workout]);

  const axiosInstance = useMemo(() => {
    const inst = axios.create({
      baseURL: "/api",
      headers: { "Content-Type": "application/json" },
    });

    inst.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        if (token) config.headers["Authorization"] = `Bearer ${token}`;
        return config;
      },
      (error) => Promise.reject(error)
    );

    inst.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const refreshToken = localStorage.getItem("refresh_token");
            const refreshResponse = await axios.post("api/refresh_tokens/", { refresh_token: refreshToken });
            if (refreshResponse.status === 200) {
              const { access_token: newToken, refresh_token: newRefreshToken } = refreshResponse.data;
              localStorage.setItem("token", newToken);
              localStorage.setItem("refresh_token", newRefreshToken);
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              return inst(originalRequest);
            }
          } catch (refreshError) {
            console.error("Failed to refresh token:", refreshError);
          }
        }
        return Promise.reject(error);
      }
    );

    return inst;
  }, []);

  const openPremiumModal = useCallback((context) => {
    try {
      premiumModalRef.current?.open?.({ context: context ? String(context) : "" });
    } catch {
      // ignore
    }
  }, []);

  const premiumBtnStyle = useMemo(
    () => ({
      background: "#faad14",
      borderColor: "#faad14",
      color: "#111",
      fontWeight: 600,
    }),
    []
  );

  const isOutOfCredits = useMemo(() => {
    if (isAdmin) return false;
    if (isPremium) return false;
    if (creditsRemaining == null) return false; // unknown: let backend decide
    return Number(creditsRemaining) <= 0;
  }, [creditsRemaining, isAdmin, isPremium]);

  const creditsLabel = useMemo(() => {
    if (isPremium) return "Unlimited credits";
    if (creditsRemaining == null) return "Monhtly credits remaining: -";
    return `Monthly credits remaining: ${Math.max(0, Number(creditsRemaining) || 0)}`;
  }, [creditsRemaining, isPremium]);

  const handleAiError = useCallback(
    (err) => {
      try {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        const code = (detail && typeof detail === "object" ? detail.code : null) || err?.response?.data?.code || null;
        if (status === 429 && code === "ai_credits_exhausted") {
          const cr = detail && typeof detail === "object" ? detail.credits_remaining : null;
          const cl = detail && typeof detail === "object" ? detail.credits_limit : null;
          if (typeof cr === "number") setCreditsRemaining(cr);
          if (typeof cl === "number") setCreditsLimit(cl);
          Modal.confirm({
            title: "AI credits used up",
            content:
              "You’ve used all your AI credits for this month. Try again next month, or upgrade to Premium for unlimited credits.",
            okText: "Upgrade Premium",
            cancelText: "Got it",
            okButtonProps: { style: premiumBtnStyle },
            onOk: () => openPremiumModal("AI credits used up (Simpl'AI Single Workout Builder)"),
          });
          return true;
        }
      } catch {
        // ignore
      }
      return false;
    },
    [openPremiumModal, premiumBtnStyle]
  );

  // Bootstrap user/account info from parent (App.js) / intro endpoint (no /get_user_info calls here).
  useEffect(() => {
    try {
      const b = bootstrap && typeof bootstrap === "object" ? bootstrap : {};
      const admin = typeof b.is_admin === "boolean" ? b.is_admin : !!appIsAdmin;
      const premium = typeof b.is_premium === "boolean" ? b.is_premium : !!appIsPremium;
      setIsAdmin(admin);
      setIsPremium(premium);
      if (b.credits_remaining !== undefined) {
        const cr = b.credits_remaining;
        setCreditsRemaining(typeof cr === "number" ? cr : cr == null ? null : Number(cr));
      }
      if (b.credits_limit !== undefined) {
        const cl = b.credits_limit;
        setCreditsLimit(typeof cl === "number" ? cl : cl == null ? null : Number(cl));
      }
      const ftp0 = b.user_ftp ?? b.userFtp ?? appFtp;
      const ftp = typeof ftp0 === "number" ? ftp0 : ftp0 == null ? null : Number(ftp0);
      if (typeof ftp === "number" && ftp > 10) {
        setDisplayFtp((prev) => (typeof prev === "number" && prev > 10 ? prev : ftp));
      }
    } catch { }
    setUserInfoLoaded(true);
  }, [bootstrap, appFtp, appIsAdmin, appIsPremium]);

  const canShowWatts = useMemo(() => typeof displayFtp === "number" && displayFtp > 10, [displayFtp]);

  // Step validation rules
  const STEP_RULES = useMemo(
    () => ({
      minSteps: 1,
      maxSteps: 100,
      durationMinSec: 10,
      durationMaxSec: 10 * 3600,
      rpmMin: 10,
      rpmMax: 300,
      wattsMin: 10,
      wattsMax: 1500,
      pctMin: 5,
      pctMax: 1000,
    }),
    []
  );

  const parseIntStrict = useCallback((s) => {
    const raw = typeof s === "string" ? s.trim() : s == null ? "" : String(s).trim();
    if (!raw) return { ok: false, value: null };
    // allow only integers (Inputs are plain text)
    const n = Number(raw);
    if (!Number.isFinite(n)) return { ok: false, value: null };
    const i = Math.trunc(n);
    return { ok: true, value: i };
  }, []);

  const powerPercentToWatts = useCallback(
    (p) => {
      const pct = typeof p === "number" ? p : Number(p);
      if (!Number.isFinite(pct)) return "";
      if (!canShowWatts) return "";
      return Math.round((pct * displayFtp) / 100);
    },
    [canShowWatts, displayFtp]
  );

  const powerWattsToPercent = useCallback(
    (w) => {
      const watts = typeof w === "number" ? w : Number(w);
      if (!Number.isFinite(watts)) return 0;
      if (!canShowWatts) return 0;
      return Math.round((watts * 100) / displayFtp);
    },
    [canShowWatts, displayFtp]
  );

  const getDraft = useCallback(
    (idx, field) => {
      const d = stepDrafts && typeof stepDrafts === "object" ? stepDrafts : {};
      const row = d[idx] && typeof d[idx] === "object" ? d[idx] : null;
      return row && Object.prototype.hasOwnProperty.call(row, field) ? row[field] : undefined;
    },
    [stepDrafts]
  );

  const setDraft = useCallback((idx, field, value) => {
    setStepDrafts((prev) => {
      const next = { ...(prev || {}) };
      const row = { ...((next[idx] && typeof next[idx] === "object" ? next[idx] : {}) || {}) };
      row[field] = value;
      next[idx] = row;
      return next;
    });
  }, []);

  const clearDraft = useCallback((idx, field) => {
    setStepDrafts((prev) => {
      const cur = prev && typeof prev === "object" ? prev : {};
      if (!Object.prototype.hasOwnProperty.call(cur, idx)) return prev;
      const row = cur[idx] && typeof cur[idx] === "object" ? { ...cur[idx] } : {};
      if (!Object.prototype.hasOwnProperty.call(row, field)) return prev;
      delete row[field];
      const next = { ...cur };
      if (Object.keys(row).length === 0) {
        delete next[idx];
      } else {
        next[idx] = row;
      }
      return next;
    });
  }, []);

  const validatePercent = useCallback(
    (pct) => {
      if (!Number.isFinite(pct)) return `Enter a number (${STEP_RULES.pctMin}–${STEP_RULES.pctMax}% FTP)`;
      if (pct < STEP_RULES.pctMin || pct > STEP_RULES.pctMax) return `Must be ${STEP_RULES.pctMin}–${STEP_RULES.pctMax}%`;
      return "";
    },
    [STEP_RULES]
  );

  const validateWatts = useCallback(
    (w) => {
      if (!Number.isFinite(w)) return `Enter a number (${STEP_RULES.wattsMin}–${STEP_RULES.wattsMax}W)`;
      if (w < STEP_RULES.wattsMin || w > STEP_RULES.wattsMax) return `Must be ${STEP_RULES.wattsMin}–${STEP_RULES.wattsMax}W`;
      return "";
    },
    [STEP_RULES]
  );

  const validateDuration = useCallback(
    (sec) => {
      if (!Number.isFinite(sec)) return `Enter seconds (${STEP_RULES.durationMinSec}–${STEP_RULES.durationMaxSec})`;
      if (sec < STEP_RULES.durationMinSec || sec > STEP_RULES.durationMaxSec)
        return `Must be ${STEP_RULES.durationMinSec}s–${STEP_RULES.durationMaxSec}s`;
      return "";
    },
    [STEP_RULES]
  );

  const validateRpm = useCallback(
    (rpm) => {
      if (!Number.isFinite(rpm)) return `Enter RPM (${STEP_RULES.rpmMin}–${STEP_RULES.rpmMax}) or leave empty`;
      if (rpm === 0) return ""; // "none"
      if (rpm < STEP_RULES.rpmMin || rpm > STEP_RULES.rpmMax) return `Must be ${STEP_RULES.rpmMin}–${STEP_RULES.rpmMax} RPM`;
      return "";
    },
    [STEP_RULES]
  );

  const getFieldError = useCallback(
    (idx, field) => {
      const steps = Array.isArray(workout?.steps) ? workout.steps : [];
      const st = steps[idx] || {};
      if (field === "duration") {
        const d = getDraft(idx, "duration");
        const src = d !== undefined ? d : st?.duration;
        const parsed = parseIntStrict(src);
        return validateDuration(parsed.ok ? parsed.value : NaN);
      }
      if (field === "rpm") {
        const d = getDraft(idx, "rpm");
        const src = d !== undefined ? d : st?.rpm;
        // Optional: allow empty/0 as "none"
        if (src == null) return "";
        if (typeof src === "string" && !src.trim()) return "";
        const parsed = parseIntStrict(src);
        if (parsed.ok && parsed.value === 0) return "";
        return validateRpm(parsed.ok ? parsed.value : NaN);
      }
      if (field === "watt") {
        // Always stored as %FTP (ref_ftp=100). If display FTP is provided, also ensure computed Watts is reasonable.
        const pctStored = Number(st?.watt);
        const pctErr = validatePercent(Number.isFinite(pctStored) ? pctStored : NaN);

        const d = getDraft(idx, "watt");
        // If user is editing, validate based on current display mode.
        if (d !== undefined) {
          const parsed = parseIntStrict(d);
          if (!parsed.ok) return powerDisplayMode === "watts" ? `Enter Watts (${STEP_RULES.wattsMin}–${STEP_RULES.wattsMax})` : `Enter %FTP (${STEP_RULES.pctMin}–${STEP_RULES.pctMax})`;
          if (powerDisplayMode === "watts") {
            if (!canShowWatts) return "Enter Display FTP to edit in Watts";
            const wErr = validateWatts(parsed.value);
            if (wErr) return wErr;
            const pct = powerWattsToPercent(parsed.value);
            const pctFromWErr = validatePercent(Number.isFinite(pct) ? pct : NaN);
            return pctFromWErr || "";
          }
          const pctErr2 = validatePercent(parsed.value);
          if (pctErr2) return pctErr2;
          // Also check watts if we have an FTP
          if (canShowWatts) {
            const w = powerPercentToWatts(parsed.value);
            const wErr2 = validateWatts(Number(w));
            if (wErr2) return wErr2;
          }
          return "";
        }

        // No draft: validate stored %FTP, and optionally watts derived from stored %FTP when FTP is known.
        if (pctErr) return pctErr;
        if (canShowWatts) {
          const w = powerPercentToWatts(pctStored);
          const wErr = validateWatts(Number(w));
          if (wErr) return wErr;
        }
        return "";
      }
      return "";
    },
    [
      STEP_RULES,
      canShowWatts,
      getDraft,
      parseIntStrict,
      powerDisplayMode,
      powerPercentToWatts,
      powerWattsToPercent,
      validateDuration,
      validatePercent,
      validateRpm,
      validateWatts,
      workout?.steps,
    ]
  );

  const hasAnyStepError = useMemo(() => {
    const steps = Array.isArray(workout?.steps) ? workout.steps : [];
    if (steps.length < STEP_RULES.minSteps || steps.length > STEP_RULES.maxSteps) return true;
    for (let i = 0; i < steps.length; i++) {
      if (getFieldError(i, "duration")) return true;
      if (getFieldError(i, "watt")) return true;
      if (getFieldError(i, "rpm")) return true;
    }
    return false;
  }, [STEP_RULES, getFieldError, workout?.steps]);

  const ensureStepCount = useCallback(
    (nextSteps) => {
      const n = Array.isArray(nextSteps) ? nextSteps.length : 0;
      if (n < STEP_RULES.minSteps) return `Workout must have at least ${STEP_RULES.minSteps} step.`;
      if (n > STEP_RULES.maxSteps) return `Workout can have at most ${STEP_RULES.maxSteps} steps.`;
      return "";
    },
    [STEP_RULES]
  );

  const canGoToWorkoutStep = useMemo(() => {
    // Only allow generating when:
    // - user already sent something
    // - we have a reformulation
    // - coach said it's enough
    // - and we are NOT currently waiting for the coach reply
    if (sending) return false;
    if (!hasAnyUserMessage) return false;
    if (!lastReformulation.trim()) return false;
    if (!intakeIsEnough) return false;
    return true;
  }, [sending, hasAnyUserMessage, lastReformulation, intakeIsEnough]);

  // On resume, we may already have a generated workout even if intake flags weren't restored perfectly.
  // In that case, we still want to allow "Regenerate (overwrite)" so the user can iterate.
  const canGenerateOrRegenerate = useMemo(() => {
    if (generating) return false;
    return canGoToWorkoutStep || !!workout;
  }, [canGoToWorkoutStep, generating, workout]);

  // Restore from saved state (App state / Firestore-hydrated)
  useEffect(() => {
    if (restoredFromStateRef.current) return;
    if (!initialState || typeof initialState !== "object") return;
    restoredFromStateRef.current = true;

    const ss = initialState.single_session && typeof initialState.single_session === "object" ? initialState.single_session : null;
    let restoredConversation = [];
    let restoredReformulation = "";

    try {
      const step = Number(initialState.current_step);
      if (!Number.isNaN(step) && step >= 0 && step <= 1) setCurrentStep(step);
    } catch { }

    try {
      const conv = Array.isArray(initialState.conversation) ? initialState.conversation : [];
      const normalized = conv
        .filter((m) => m && typeof m === "object" && (m.role === "user" || m.role === "assistant") && typeof m.content === "string")
        .map((m) => ({ role: m.role, content: m.content }));
      if (normalized.length) {
        restoredConversation = normalized;
        setChatMessages(normalized);
      }
    } catch { }

    try {
      if (typeof initialState.last_reformulation === "string") {
        restoredReformulation = initialState.last_reformulation.trim();
      }
    } catch { }
    // Backward/loose compatibility: try to recover reformulation from the assistant message template.
    if (!restoredReformulation && restoredConversation.length) {
      try {
        const marker = "Here is your request (reformulated):";
        for (let i = restoredConversation.length - 1; i >= 0; i--) {
          const m = restoredConversation[i];
          if (!m || m.role !== "assistant") continue;
          const c = String(m.content || "");
          const idx = c.indexOf(marker);
          if (idx === -1) continue;
          let after = c.slice(idx + marker.length);
          after = after.replace(/^\s*\n/, "");
          const firstPara = after.split("\n\n")[0].trim();
          if (firstPara) {
            restoredReformulation = firstPara;
            break;
          }
        }
      } catch { }
    }
    if (restoredReformulation) {
      try {
        setLastReformulation(restoredReformulation);
      } catch { }
    }

    try {
      const enough = ss?.intake_is_enough ?? initialState.intake_is_enough;
      if (typeof enough === "boolean") setIntakeIsEnough(enough);
    } catch { }
    try {
      if (typeof initialState.generate_note === "string") setGenerateNote(initialState.generate_note);
    } catch { }
    try {
      const cc = typeof ss?.coach_comment === "string" ? ss.coach_comment : initialState.coach_comment;
      if (typeof cc === "string") setCoachComment(cc);
    } catch { }
    try {
      const cat = typeof ss?.category === "string" ? ss.category : initialState.category;
      if (typeof cat === "string" && cat.trim()) setCategory(cat.trim());
    } catch { }
    try {
      const wkt = ss?.workout && typeof ss.workout === "object" ? ss.workout : initialState.workout;
      if (wkt && typeof wkt === "object") {
        setWorkout(wkt);
        // If a workout exists, we consider intake complete enough to allow regenerating/iterating.
        try {
          setIntakeIsEnough(true);
        } catch { }
        // Last resort: if reformulation is still missing, build a minimal prompt from the workout itself.
        if (!restoredReformulation) {
          try {
            const title = String(wkt?.title || "").trim();
            const desc = String(wkt?.description || "").trim();
            const gn = typeof initialState.generate_note === "string" ? initialState.generate_note.trim() : "";
            const parts = ["Build a cycling ERG workout similar to the one previously generated."];
            if (title) parts.push(`Title: ${title}.`);
            if (desc) parts.push(`Description: ${desc}.`);
            if (gn) parts.push(`Regeneration note: ${gn}.`);
            const fallback = parts.join(" ").trim();
            if (fallback) setLastReformulation(fallback);
          } catch { }
        }
      }
    } catch { }

    // Admin-only debug restore (best-effort)
    try {
      const dbg = initialState.debug && typeof initialState.debug === "object" ? initialState.debug : null;
      if (dbg) {
        if (dbg.intake) setDebugIntake(dbg.intake);
        if (dbg.generate) setDebugGenerate(dbg.generate);
      }
    } catch { }
  }, [initialState]);

  // Seed intro message once
  useEffect(() => {
    if (hasBootstrappedRef.current) return;
    hasBootstrappedRef.current = true;
    if (restoredFromStateRef.current) return;
    const intro = (initialCoachMessage || "").trim();
    if (!intro) return;
    setChatMessages((prev) => (prev.length ? prev : [{ role: "assistant", content: intro }]));
  }, [initialCoachMessage]);

  // Auto-scroll conversation to newest message
  useEffect(() => {
    if (!convoScrollRef.current) return;
    try {
      convoScrollRef.current.scrollTop = convoScrollRef.current.scrollHeight;
    } catch {
      // ignore
    }
  }, [chatMessages.length]);

  // Keep parent (App state) in sync so leaving/re-entering without refetch still resumes.
  useEffect(() => {
    if (typeof onPersistState !== "function") return;
    const t = setTimeout(() => {
      emitPersistState();
    }, 250);
    return () => clearTimeout(t);
  }, [emitPersistState, onPersistState]);

  const sendCoachMessage = useCallback(async () => {
    const userText = (chatInput || "").trim();
    if (!userText) return;
    if (isOutOfCredits) {
      openPremiumModal("Simpl'AI Single Workout Builder - Intake - out of credits");
      return;
    }
    const nextMessages = [...chatMessages, { role: "user", content: userText }];
    setChatMessages(nextMessages);
    setChatInput("");
    setSending(true);
    setIntakeIsEnough(false);

    try {
      const resp = await axiosInstance.post("/ai/plan_builder/single_session/step1/", {
        user_message: userText,
        conversation: nextMessages,
        last_reformulation: lastReformulation || undefined,
      });
      const data = resp?.data || {};
      try {
        if (typeof data?.credits_remaining === "number") setCreditsRemaining(data.credits_remaining);
        if (typeof data?.credits_limit === "number") setCreditsLimit(data.credits_limit);
        if (typeof data?.is_premium === "boolean") setIsPremium(data.is_premium);
      } catch { }
      const reformulated = String(data.reformulated_desiderata || "").trim();
      const whatsMissing = String(data.whats_missing || "").trim();
      const dbg = data.debug ? capDebug(data.debug) : null;
      const adminForThis = isAdmin || !!dbg;
      if (dbg) setIsAdmin(true);

      setLastReformulation(reformulated);
      setIntakeIsEnough(!!data.is_enough);
      if (dbg) setDebugIntake(dbg);

      const assistantText = data?.is_enough
        ? `Here is your request (reformulated):\n${reformulated}\n\nReady to generate your workout → go to the Workout step.\n\nOptional refinements / advice:\n${whatsMissing || "- If you want tweaks, tell me now (e.g., easier/harder, more cadence work, longer warmup)."
        }`
        : `Here is what I can build from what you said:\n${reformulated}\n\nI can already generate a workout now. If you want it more personalized, answer any of these (optional):\n${whatsMissing || "- Duration (minutes)\n- Easy/moderate/hard\n- Any constraints/injuries\n- HR + cadence sensors?"
        }\n\nOtherwise just click "Generate my workout".`;

      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: assistantText,
          meta: {
            is_enough: !!data.is_enough,
            whats_missing: whatsMissing,
            reformulated_desiderata: reformulated,
          },
        },
      ]);

      emitPersistState({
        status: "in_progress",
        mode: "single_session",
        current_step: 0,
        conversation: [...nextMessages, { role: "assistant", content: assistantText }],
        last_reformulation: reformulated,
        intake_is_enough: !!data.is_enough,
        debug: adminForThis
          ? {
            intake: dbg || debugIntake,
            generate: debugGenerate,
          }
          : undefined,
      });
    } catch (err) {
      console.error("AI single-session step1 error:", err);
      if (!handleAiError(err)) message.error("Couldn’t reach the AI right now. Please try again.");
    } finally {
      setSending(false);
    }
  }, [axiosInstance, chatInput, chatMessages, lastReformulation, isAdmin, capDebug, emitPersistState, debugIntake, debugGenerate, isOutOfCredits, openPremiumModal, handleAiError]);

  const generateWorkout = useCallback(async () => {
    // If we are resuming with an existing workout, allow iteration even if intake flags weren't restored.
    const allowBypassIntake = !!workout;
    const effectiveIntakeEnough = intakeIsEnough || allowBypassIntake;

    let effectiveReformulation = String(lastReformulation || "").trim();
    if (!effectiveReformulation) {
      // Attempt to recover from conversation template (best-effort).
      try {
        const marker = "Here is your request (reformulated):";
        for (let i = (chatMessages || []).length - 1; i >= 0; i--) {
          const m = chatMessages[i];
          if (!m || m.role !== "assistant") continue;
          const c = String(m.content || "");
          const idx = c.indexOf(marker);
          if (idx === -1) continue;
          let after = c.slice(idx + marker.length);
          after = after.replace(/^\s*\n/, "");
          const firstPara = after.split("\n\n")[0].trim();
          if (firstPara) {
            effectiveReformulation = firstPara;
            break;
          }
        }
      } catch { }
    }
    if (!effectiveReformulation && workout) {
      // Last resort fallback: use workout details as a minimal prompt.
      try {
        const title = String(workout?.title || "").trim();
        const desc = String(workout?.description || "").trim();
        const parts = ["Build a cycling ERG workout similar to the one previously generated."];
        if (title) parts.push(`Title: ${title}.`);
        if (desc) parts.push(`Description: ${desc}.`);
        effectiveReformulation = parts.join(" ").trim();
      } catch { }
    }

    if (!effectiveIntakeEnough) {
      message.error("Please finish the intake first (wait for the coach to confirm it has enough info).");
      return;
    }
    if (!effectiveReformulation) {
      message.error("Missing reformulated request. Please go back to the intake step and send one message to rebuild context.");
      return;
    }
    if (isOutOfCredits) {
      openPremiumModal("Simpl'AI Single Workout Builder - Generate workout - out of credits");
      return;
    }
    setGenerating(true);
    try {
      // Keep local state consistent if we had to recover the reformulation.
      try {
        if (!String(lastReformulation || "").trim()) setLastReformulation(effectiveReformulation);
      } catch { }
      const resp = await axiosInstance.post("/ai/plan_builder/single_session/generate/", {
        reformulated_desiderata: effectiveReformulation,
        comment: generateNote.trim() || undefined,
        previous_workout: workout || undefined,
        category: category.trim() || undefined,
      });
      try {
        if (typeof resp?.data?.credits_remaining === "number") setCreditsRemaining(resp.data.credits_remaining);
        if (typeof resp?.data?.credits_limit === "number") setCreditsLimit(resp.data.credits_limit);
        if (typeof resp?.data?.is_premium === "boolean") setIsPremium(resp.data.is_premium);
      } catch { }
      const wkt = resp?.data?.workout;
      const cc = String(resp?.data?.coach_comment || "").trim();
      const dbg = resp?.data?.debug ? capDebug(resp.data.debug) : null;
      const adminForThis = isAdmin || !!dbg;
      if (dbg) setIsAdmin(true);
      if (!wkt) throw new Error("Missing workout in response");
      setWorkout(wkt);
      setCoachComment(cc);
      if (dbg) setDebugGenerate(dbg);
      if (wkt?.category && typeof wkt.category === "string" && wkt.category.trim()) {
        setCategory(wkt.category.trim());
      }
      emitPersistState({
        status: "in_progress",
        mode: "single_session",
        current_step: 1,
        last_reformulation: effectiveReformulation,
        generate_note: generateNote,
        coach_comment: cc,
        category: (wkt?.category && typeof wkt.category === "string" && wkt.category.trim()) ? wkt.category.trim() : category,
        workout: wkt,
        debug: adminForThis
          ? {
            intake: debugIntake,
            generate: dbg || debugGenerate,
          }
          : undefined,
      });
      message.success("Workout generated.");
    } catch (e) {
      console.error("AI single-session generate error:", e);
      if (!handleAiError(e)) message.error("Failed to generate workout. Please try again.");
    } finally {
      setGenerating(false);
    }
  }, [axiosInstance, category, chatMessages, generateNote, intakeIsEnough, lastReformulation, workout, isAdmin, capDebug, emitPersistState, debugIntake, debugGenerate, isOutOfCredits, openPremiumModal, handleAiError]);

  const saveWorkout = useCallback(async () => {
    if (!workout) return;
    // Block saving if any invalid edits exist (show modal instead of spamming toasts).
    try {
      const steps = Array.isArray(workout?.steps) ? workout.steps : [];
      const stepCountErr = ensureStepCount(steps);
      if (stepCountErr || hasAnyStepError) {
        Modal.error({
          title: "Fix invalid values",
          content: (
            <span style={{ whiteSpace: "pre-wrap" }}>
              {"Some values are incorrect and should be fixed.\n\nSee the red fields and their comments."}
            </span>
          ),
          okText: "Got it",
        });
        return;
      }
    } catch {
      // if anything unexpected happens, fail closed (do not save)
      Modal.error({
        title: "Fix invalid values",
        content: (
          <span style={{ whiteSpace: "pre-wrap" }}>
            {"Some values are incorrect and should be fixed.\n\nSee the red fields and their comments."}
          </span>
        ),
        okText: "Got it",
      });
      return;
    }
    const title = String(workout.title || "").trim() || "Workout";
    const cat = String(category || "").trim() || "AI Single Sessions";
    setSaving(true);
    try {
      const descriptionParts = [];
      if (coachComment) descriptionParts.push(coachComment);
      if (workout.description) descriptionParts.push(workout.description);
      if (generateNote.trim()) descriptionParts.push(`Generation note: ${generateNote.trim()}`);

      const payload = {
        name: title,
        category: cat,
        description: descriptionParts.filter(Boolean).join("\n\n"),
        ref_ftp: 100,
        stress_points: workout.stress_points || undefined,
        steps: (workout.steps || []).map((st) => ({
          title: st.title,
          comment: st.comment,
          duration: st.duration,
          watt: st.watt, // %FTP
          rpm: st.rpm,
        })),
      };
      const resp = await axiosInstance.post("/save_training_plan/", payload);
      const savedUuid = resp?.data?.uuid || null;
      message.success(`Saved. Available under "${cat} - ${title}". It will be loaded automatically in a few seconds.`);

      // Mark the single-session builder as completed (so UI won't offer "Resume" next time).
      try {
        await axiosInstance.post("/ai/plan_builder/single_session/complete/", {});
      } catch {
        // ignore
      }
      try {
        if (typeof onPersistState === "function") onPersistState(null);
      } catch {
        // ignore
      }
      try {
        if (typeof onAfterSave === "function") onAfterSave(savedUuid);
      } catch {
        // ignore
      }
      onClose?.();
    } catch (e) {
      console.error("Save single session error:", e);
      message.error(e?.response?.data?.detail || e?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  }, [axiosInstance, category, coachComment, ensureStepCount, generateNote, hasAnyStepError, onAfterSave, onClose, onPersistState, workout]);

  const saveDraft = useCallback(
    async ({ closeAfter = false } = {}) => {
      // Persist locally (App state + localStorage) AND remotely (Firestore) so user can resume later.
      setSavingDraft(true);
      try {
        emitPersistState({
          status: "in_progress",
          mode: "single_session",
          current_step: currentStep,
        });

        await axiosInstance.post("/ai/plan_builder/single_session/save_draft/", {
          status: "in_progress",
          mode: "single_session",
          current_step: currentStep,
          intake_is_enough: intakeIsEnough,
          conversation: (chatMessages || [])
            .map((m) => ({ role: m?.role, content: m?.content }))
            .filter((m) => (m.role === "user" || m.role === "assistant") && typeof m.content === "string"),
          last_reformulation: lastReformulation || null,
          generate_note: generateNote || null,
          single_session: {
            category: (category || "").trim() || "AI Single Sessions",
            coach_comment: (coachComment || "").trim() || null,
            workout: workout || null,
          },
        });

        // After successful draft save, clear "dirty" state.
        setDraftBaselineToCurrent();

        message.success("Saved draft. You can resume editing later.");
        if (closeAfter) doLeaveNoPrompt();
      } catch (e) {
        console.error("Save single session draft error:", e);
        message.error(e?.response?.data?.detail || e?.message || "Failed to save draft");
      } finally {
        setSavingDraft(false);
      }
    },
    [
      axiosInstance,
      category,
      chatMessages,
      coachComment,
      currentStep,
      doLeaveNoPrompt,
      emitPersistState,
      generateNote,
      intakeIsEnough,
      lastReformulation,
      setDraftBaselineToCurrent,
      workout,
    ]
  );

  const resumeEditingLater = useCallback(async () => {
    await saveDraft({ closeAfter: true });
  }, [saveDraft]);

  const handleLeave = useCallback(() => {
    // Always implicit: leaving behaves like "Resume editing later"
    saveDraft({ closeAfter: true });
  }, [saveDraft]);

  const workoutSummary = useMemo(() => {
    const steps = Array.isArray(workout?.steps) ? workout.steps : [];
    const totalSec = steps.reduce((sum, s) => sum + (Number(s?.duration) || 0), 0);
    const durationMin = totalSec > 0 ? Math.round(totalSec / 60) : null;
    const rawTss = workout?.stress_points;
    const numericTss = typeof rawTss === "number" ? rawTss : rawTss != null ? Number(rawTss) : null;

    let computedTss = null;
    try {
      let totalDuration = 0;
      let intensityFactorSum = 0;
      steps.forEach((st) => {
        const dur = Number(st?.duration) || 0;
        const p = Number(st?.watt ?? st?.watt_percent) || 0; // both are %FTP for this builder
        if (dur > 0 && p > 0) {
          const ifac = p / 100;
          intensityFactorSum += ifac ** 2 * dur;
          totalDuration += dur;
        }
      });
      if (totalDuration > 0) {
        const normalizedPower = Math.sqrt(intensityFactorSum / totalDuration) * 100; // FTP=100 reference
        const tss = (totalDuration * normalizedPower ** 2) / (100 ** 2 * 3600) * 100;
        computedTss = Math.round(tss);
      }
    } catch {
      computedTss = null;
    }

    const tss = Number.isFinite(numericTss) ? Math.round(numericTss) : computedTss;
    return { totalSec, durationMin, tss };
  }, [workout]);

  const columns = useMemo(
    () => [
      { title: "#", key: "idx", width: 60, render: (val, record, i) => i + 1 },
      {
        title: "Title",
        dataIndex: "title",
        key: "title",
        width: 200,
        render: (value, record, i) => (
          <Input
            value={value || ""}
            onChange={(e) => {
              const v = e.target.value;
              setWorkout((prev) => {
                if (!prev) return prev;
                const steps = Array.isArray(prev.steps) ? [...prev.steps] : [];
                steps[i] = { ...(steps[i] || {}), title: v };
                return { ...prev, steps };
              });
            }}
          />
        ),
      },
      {
        title: "Duration (sec)",
        dataIndex: "duration",
        key: "duration",
        width: 140,
        render: (value, record, i) => {
          const d = getDraft(i, "duration");
          const displayValue = d !== undefined ? d : value ?? "";
          const err = getFieldError(i, "duration");
          return (
            <div>
              <Input
                status={err ? "error" : undefined}
                value={displayValue}
                onChange={(e) => {
                  const raw = e.target.value;
                  setDraft(i, "duration", raw);
                  const parsed = parseIntStrict(raw);
                  const nextErr = validateDuration(parsed.ok ? parsed.value : NaN);
                  if (!nextErr) {
                    setWorkout((prev) => {
                      if (!prev) return prev;
                      const steps = Array.isArray(prev.steps) ? [...prev.steps] : [];
                      steps[i] = { ...(steps[i] || {}), duration: parsed.value };
                      return { ...prev, steps };
                    });
                    clearDraft(i, "duration");
                  }
                }}
              />
              {err ? (
                <Text type="danger" style={{ fontSize: 12 }}>
                  {err}
                </Text>
              ) : null}
            </div>
          );
        },
      },
      {
        title: powerDisplayMode === "watts" ? "Power (W)" : "Power (%FTP)",
        dataIndex: "watt",
        key: "watt",
        width: 130,
        render: (value, record, i) => {
          const d = getDraft(i, "watt");
          const basePct = typeof value === "number" ? value : Number(value);
          const computedWatts = powerPercentToWatts(Number.isFinite(basePct) ? basePct : NaN);
          const displayValue = d !== undefined ? d : powerDisplayMode === "watts" ? (computedWatts || "") : (value ?? "");
          const err = getFieldError(i, "watt");
          return (
            <div>
              <Input
                status={err ? "error" : undefined}
                value={displayValue}
                onChange={(e) => {
                  const raw = e.target.value;
                  setDraft(i, "watt", raw);
                  const parsed = parseIntStrict(raw);
                  if (!parsed.ok) return; // keep draft + error

                  if (powerDisplayMode === "watts") {
                    if (!canShowWatts) return; // keep draft + error
                    const wErr = validateWatts(parsed.value);
                    if (wErr) return;
                    const pct = powerWattsToPercent(parsed.value);
                    const pctErr = validatePercent(pct);
                    if (pctErr) return;
                    setWorkout((prev) => {
                      if (!prev) return prev;
                      const steps = Array.isArray(prev.steps) ? [...prev.steps] : [];
                      steps[i] = { ...(steps[i] || {}), watt: pct };
                      return { ...prev, steps };
                    });
                    clearDraft(i, "watt");
                    return;
                  }

                  const pctErr = validatePercent(parsed.value);
                  if (pctErr) return;
                  // Optional: if we have an FTP, also enforce the implied watts range.
                  if (canShowWatts) {
                    const w = powerPercentToWatts(parsed.value);
                    const wErr2 = validateWatts(Number(w));
                    if (wErr2) return;
                  }
                  setWorkout((prev) => {
                    if (!prev) return prev;
                    const steps = Array.isArray(prev.steps) ? [...prev.steps] : [];
                    steps[i] = { ...(steps[i] || {}), watt: parsed.value };
                    return { ...prev, steps };
                  });
                  clearDraft(i, "watt");
                }}
              />
              {err ? (
                <Text type="danger" style={{ fontSize: 12 }}>
                  {err}
                </Text>
              ) : null}
            </div>
          );
        },
      },
      {
        title: "RPM",
        dataIndex: "rpm",
        key: "rpm",
        width: 90,
        render: (value, record, i) => {
          const d = getDraft(i, "rpm");
          const displayValue = d !== undefined ? d : (Number(value) > 0 ? value : "");
          const err = getFieldError(i, "rpm");
          return (
            <div>
              <Input
                status={err ? "error" : undefined}
                value={displayValue}
                onChange={(e) => {
                  const raw = e.target.value;
                  setDraft(i, "rpm", raw);
                  const trimmed = String(raw || "").trim();
                  // empty/0 => "none"
                  if (!trimmed || trimmed === "0") {
                    setWorkout((prev) => {
                      if (!prev) return prev;
                      const steps = Array.isArray(prev.steps) ? [...prev.steps] : [];
                      steps[i] = { ...(steps[i] || {}), rpm: 0 };
                      return { ...prev, steps };
                    });
                    clearDraft(i, "rpm");
                    return;
                  }
                  const parsed = parseIntStrict(raw);
                  const nextErr = validateRpm(parsed.ok ? parsed.value : NaN);
                  if (!nextErr) {
                    setWorkout((prev) => {
                      if (!prev) return prev;
                      const steps = Array.isArray(prev.steps) ? [...prev.steps] : [];
                      steps[i] = { ...(steps[i] || {}), rpm: parsed.value };
                      return { ...prev, steps };
                    });
                    clearDraft(i, "rpm");
                  }
                }}
              />
              {err ? (
                <Text type="danger" style={{ fontSize: 12 }}>
                  {err}
                </Text>
              ) : null}
            </div>
          );
        },
      },
      {
        title: "Comment",
        dataIndex: "comment",
        key: "comment",
        render: (value, record, i) => (
          <Input
            value={value || ""}
            onChange={(e) => {
              const v = e.target.value;
              setWorkout((prev) => {
                if (!prev) return prev;
                const steps = Array.isArray(prev.steps) ? [...prev.steps] : [];
                steps[i] = { ...(steps[i] || {}), comment: v };
                return { ...prev, steps };
              });
            }}
          />
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 70,
        render: (value, record, i) => (
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            title="Remove step"
            aria-label="Remove step"
            onClick={() => {
              setWorkout((prev) => {
                if (!prev) return prev;
                const steps = Array.isArray(prev.steps) ? [...prev.steps] : [];
                if (steps.length <= STEP_RULES.minSteps) return prev;
                steps.splice(i, 1);
                return { ...prev, steps };
              });
              // clear drafts to avoid index shifts
              setStepDrafts({});
            }}
            disabled={(Array.isArray(workout?.steps) ? workout.steps.length : 0) <= STEP_RULES.minSteps}
          />
        ),
      },
    ],
    [
      STEP_RULES,
      canShowWatts,
      clearDraft,
      getDraft,
      getFieldError,
      parseIntStrict,
      powerDisplayMode,
      powerPercentToWatts,
      powerWattsToPercent,
      setDraft,
      validateDuration,
      validatePercent,
      validateRpm,
      validateWatts,
      workout?.steps,
    ]
  );

  const Step1 = (
    <Space direction="vertical" style={{ width: "100%" }} size={12}>
      <Alert
        type="info"
        showIcon
        message="Create one workout session"
        description="Tell the coach what you want for today (goal + duration + how hard). The coach will reformulate it and ask for anything missing."
      />

      <Card size="small" title="Conversation">
        {chatMessages.length === 0 ? (
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Describe the workout you want to do today.
          </Paragraph>
        ) : (
          <div ref={convoScrollRef} style={{ maxHeight: 360, overflowY: "auto", paddingRight: 6 }}>
            {chatMessages.map((m, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  justifyContent: m.role === "user" ? "flex-end" : "flex-start",
                  padding: "8px 0",
                }}
              >
                <div
                  style={{
                    maxWidth: "82%",
                    padding: "10px 12px",
                    borderRadius: 12,
                    background: m.role === "user" ? "#e6f4ff" : "#f5f5f5",
                    border: "1px solid rgba(0,0,0,0.06)",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  <Text strong style={{ display: "block", marginBottom: 4 }}>
                    {m.role === "user" ? "You" : "Simpl'AI Coach"}
                  </Text>
                  <Text>{renderMarkdownLite(m.content)}</Text>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {isAdmin ? <DebugPanel debug={debugIntake} /> : null}

      <Card size="small" title="Your message">
        <Space direction="vertical" style={{ width: "100%" }}>
          <TextArea
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            rows={4}
            placeholder="Example: Build me a 50min sweet spot workout (moderate-hard). I have a smart trainer + cadence + HR. Keep the warmup long. No sprints."
            onPressEnter={(e) => {
              if (e.shiftKey) return;
              e.preventDefault();
              sendCoachMessage();
            }}
          />
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Button onClick={handleLeave}>Leave</Button>
            <Space wrap>
              <Button type="primary" icon={<SendOutlined />} onClick={sendCoachMessage} loading={sending} disabled={!chatInput.trim()}>
                Send
              </Button>
              <Button
                type="primary"
                disabled={!canGoToWorkoutStep}
                onClick={() => {
                  setCurrentStep(1);
                }}
              >
                Generate my workout
              </Button>
            </Space>
          </Space>
        </Space>
      </Card>
    </Space>
  );

  const Step2 = (
    <Space direction="vertical" style={{ width: "100%" }} size={12}>
      <Alert
        type="info"
        showIcon
        message="Workout (single session)"
        description="Generate one detailed ERG workout (all steps in %FTP). You can regenerate and edit before saving."
      />

      {isOutOfCredits ? (
        <Alert
          type="warning"
          showIcon
          message="You’ve used all your AI credits for this month"
          description={
            <Space wrap>
              <span>Try again next month, or upgrade to Premium for unlimited credits.</span>
              <RequestPremium
                axiosInstance={axiosInstance}
                buttonText="Upgrade Premium"
                buttonProps={{ style: premiumBtnStyle }}
                context="Simpl'AI Single Workout Builder - out of credits"
              />
            </Space>
          }
        />
      ) : null}

      <Card size="small" title="Generation settings">
        <Space direction="vertical" style={{ width: "100%" }} size={10}>
          {!lastReformulation.trim() && !workout ? (
            <Text type="secondary">Go back to intake and describe your workout first.</Text>
          ) : (
            <>
              {lastReformulation.trim() ? (
                <div>
                  <Text strong>Reformulated request</Text>
                  <Paragraph style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}>{lastReformulation}</Paragraph>
                </div>
              ) : (
                <Text type="secondary">
                  Resumed workout found. You can regenerate (overwrite) to iterate.
                </Text>
              )}
              <Divider style={{ margin: "10px 0" }} />
              <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
                <Button type="primary" loading={generating} onClick={generateWorkout} disabled={generating || !canGenerateOrRegenerate}>
                  {workout ? "Regenerate (overwrite)" : "Generate workout"}
                </Button>
              </Space>
              <div>
                <Text strong>Regenerate note (optional)</Text>
                <TextArea
                  value={generateNote}
                  onChange={(e) => setGenerateNote(e.target.value)}
                  rows={2}
                  placeholder="Example: easier; more cadence drills; longer warmup; fewer VO2 reps; no out-of-saddle..."
                />
              </div>
              <div>
                <Text strong>Category (for saving)</Text>
                <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="AI Single Sessions" />
              </div>
            </>
          )}
        </Space>
      </Card>

      {isAdmin ? <DebugPanel debug={debugGenerate} /> : null}

      {generating && (
        <Card size="small">
          <div style={{ padding: 14, display: "flex", alignItems: "center", gap: 12 }}>
            <Spin />
            <Text>Generating your workout…</Text>
          </div>
        </Card>
      )}

      {!generating && workout && (
        <Card size="small" title="Your workout">
          <Space direction="vertical" style={{ width: "100%" }} size={10}>
            {(workoutSummary.durationMin != null || workoutSummary.tss != null) && (
              <Alert
                type="info"
                showIcon
                message={
                  <span>
                    {workoutSummary.durationMin != null ? (
                      <>
                        <Text strong>Duration</Text>: {workoutSummary.durationMin} min
                      </>
                    ) : null}
                    {workoutSummary.durationMin != null && workoutSummary.tss != null ? "  •  " : null}
                    {workoutSummary.tss != null ? (
                      <>
                        <Text strong>TSS</Text>: {workoutSummary.tss}
                      </>
                    ) : null}
                  </span>
                }
              />
            )}
            {!!coachComment && (
              <Alert type="success" showIcon message="Coach note" description={<span style={{ whiteSpace: "pre-wrap" }}>{coachComment}</span>} />
            )}

            <Alert
              type="info"
              showIcon
              message="Power display: %FTP or Watts"
              description={
                <span>
                  This is only a display/editing convenience. The workout is always <b>saved as %FTP</b> with <b>ref FTP=100</b>.
                </span>
              }
            />
            <Space wrap>
              <Text type="secondary">Show power as</Text>
              <Switch
                checked={powerDisplayMode === "watts"}
                disabled={!canShowWatts}
                onChange={(checked) => setPowerDisplayMode(checked ? "watts" : "percent")}
                checkedChildren="Watts"
                unCheckedChildren="%FTP"
              />
              <Text type="secondary">Display FTP</Text>
              <InputNumber
                min={1}
                step={1}
                value={displayFtp}
                style={{ width: 120 }}
                placeholder="e.g. 250"
                onChange={(v) => {
                  const n = typeof v === "number" ? v : v == null ? null : Number(v);
                  setDisplayFtp(Number.isFinite(n) ? n : null);
                }}
              />
              {!canShowWatts ? <Text type="secondary">(enter FTP to enable Watts)</Text> : null}
            </Space>

            <div>
              <Text strong>Workout title</Text>
              <Input
                value={workout.title || ""}
                onChange={(e) => setWorkout((prev) => (prev ? { ...prev, title: e.target.value } : prev))}
                placeholder="Workout title"
              />
            </div>

            <div>
              <Text strong>Workout description</Text>
              <TextArea
                value={workout.description || ""}
                onChange={(e) => setWorkout((prev) => (prev ? { ...prev, description: e.target.value } : prev))}
                rows={3}
                placeholder="(Optional) Add your own notes"
              />
            </div>

            <Divider style={{ margin: "10px 0" }} />

            <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
              <Space wrap>
                <Button
                  onClick={() => {
                    setWorkout((prev) => {
                      if (!prev) return prev;
                      const steps = Array.isArray(prev.steps) ? [...prev.steps] : [];
                      if (steps.length >= STEP_RULES.maxSteps) return prev;
                      steps.push({
                        title: "Interval",
                        duration: STEP_RULES.durationMinSec,
                        watt: STEP_RULES.pctMin,
                        rpm: 0,
                        comment: "",
                      });
                      return { ...prev, steps };
                    });
                    setStepDrafts({});
                  }}
                  disabled={(Array.isArray(workout?.steps) ? workout.steps.length : 0) >= STEP_RULES.maxSteps}
                >
                  Add step
                </Button>
                <Text type="secondary">
                  Steps: {(Array.isArray(workout?.steps) ? workout.steps.length : 0)}/{STEP_RULES.maxSteps}
                </Text>
              </Space>
              {hasAnyStepError ? (
                <Text type="danger">Some step values are invalid (see red fields).</Text>
              ) : (
                <Text type="secondary">All step values look valid.</Text>
              )}
            </Space>

            <Table
              columns={columns}
              dataSource={Array.isArray(workout.steps) ? workout.steps : []}
              rowKey={(r, i) => `step-${i}`}
              pagination={false}
              size="small"
              scroll={{ x: 900 }}
            />

            <Divider style={{ margin: "10px 0" }} />

            <Text type="secondary">
              {powerDisplayMode === "watts" && canShowWatts ? (
                <>
                  Chart below uses <Text strong>FTP={displayFtp}</Text>, so the Y-axis is in <Text strong>Watts</Text>.
                </>
              ) : (
                <>
                  Chart below uses <Text strong>FTP=100</Text>, so the Y-axis is effectively <Text strong>%FTP</Text>.
                </>
              )}
            </Text>
            <TrainingChart
              steps={
                powerDisplayMode === "watts" && canShowWatts
                  ? (workout.steps || []).map((st) => ({
                    ...(st || {}),
                    // TrainingChart expects absolute Watts in step.watt
                    watt: Math.round(((Number(st?.watt) || 0) * displayFtp) / 100),
                  }))
                  : workout.steps || []
              }
              FTP={powerDisplayMode === "watts" && canShowWatts ? displayFtp : 100}
              isMobile={false}
            />

            <Divider style={{ margin: "10px 0" }} />

            <Space style={{ width: "100%", justifyContent: "space-between" }}>
              <Button onClick={handleBackToIntake}>Back to intake</Button>
              <Space wrap>
                <Button onClick={resumeEditingLater} loading={savingDraft} disabled={savingDraft}>
                  Resume editing later
                </Button>
                <Button type="primary" loading={saving} disabled={!workout || saving} onClick={saveWorkout}>
                  Save to library
                </Button>
              </Space>
            </Space>
          </Space>
        </Card>
      )}
    </Space>
  );

  return (
    <LiquidGlass className="glass-section" style={{ marginTop: 12, marginBottom: 32 }}>
      <RequestPremium axiosInstance={axiosInstance} hideButton ref={premiumModalRef} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
        <Space wrap align="center">
          <Text strong>Simpl'AI Single Workout Builder</Text>
          {!userInfoLoaded ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Loading account…
            </Text>
          ) : isPremium ? (
            <Tag color="gold">
              <Space size={6}>
                <StarFilled />
                <span>{creditsLabel}</span>
              </Space>
            </Tag>
          ) : (
            <Tag color={isOutOfCredits ? "red" : "blue"}>{creditsLabel}</Tag>
          )}
        </Space>
        <Space wrap>
          {userInfoLoaded && !isPremium ? (
            <RequestPremium
              axiosInstance={axiosInstance}
              buttonText="Upgrade to Premium (Unlimited)"
              buttonProps={{ style: premiumBtnStyle }}
              context="Simpl'AI Single Workout Builder"
            />
          ) : null}
          <Button danger onClick={handleLeave} loading={savingDraft} disabled={savingDraft}>
            Leave
          </Button>
        </Space>
      </div>
      <Steps current={currentStep} items={stepItems} size="small" />
      {currentStep === 0 ? Step1 : Step2}
    </LiquidGlass>
  );
}

export default AiIndividualSessionBuilder;


